import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import { authKeys } from '@/features/auth/api'
import { api, unwrap } from '@/lib/api'
import type {
  CommandType,
  Connectivity,
  DataEnvelope,
  Display,
  DisplayAssignment,
  DisplayCommand,
  DisplayEvent,
  DisplayStatus,
  DisplaySummary,
  Orientation,
  EventSeverity,
  Paginated,
  Screenshot,
} from '@/types/api'
import { hasActiveCommands } from './lib/commands'
import { nextBeforeCursor, type EventsCursor } from './lib/events'

export type DisplaySort = 'name' | '-name' | '-last_heartbeat_at' | '-created_at'

export interface DisplayListParams {
  search?: string
  location_id?: string
  group_id?: string
  tag_id?: string
  connectivity?: Connectivity
  status?: DisplayStatus
  has_warnings?: boolean
  sort?: DisplaySort
  per_page?: number
}

export interface DisplayInput {
  name: string
  location_id?: string | null
  description?: string | null
  timezone?: string | null
  orientation?: Orientation
  resolution_width?: number | null
  resolution_height?: number | null
  group_ids?: string[]
  tag_ids?: string[]
  /** Phase 6 — `displays.manage`. */
  default_channel_id?: string | null
  fallback_channel_id?: string | null
  /** Phase 12 — periodic screenshots (plan feature `screenshots`). */
  screenshot_policy?: { mode: 'on_demand' | 'interval'; interval_s?: number | null }
}

export const displayKeys = {
  all: ['displays'] as const,
  lists: () => [...displayKeys.all, 'list'] as const,
  list: (params: DisplayListParams) => [...displayKeys.lists(), params] as const,
  summary: () => [...displayKeys.all, 'summary'] as const,
  details: () => [...displayKeys.all, 'detail'] as const,
  detail: (id: string) => [...displayKeys.details(), id] as const,
  assignments: (id: string) => [...displayKeys.detail(id), 'assignments'] as const,
  commands: (id: string) => [...displayKeys.detail(id), 'commands'] as const,
  screenshots: (id: string) => [...displayKeys.detail(id), 'screenshots'] as const,
  events: (id: string, severities?: readonly EventSeverity[]) => [...displayKeys.detail(id), 'events', { severities: severities ?? null }] as const,
}

function toQuery(params: DisplayListParams) {
  return {
    search: params.search,
    location_id: params.location_id,
    group_id: params.group_id,
    tag_id: params.tag_id,
    connectivity: params.connectivity,
    status: params.status,
    has_warnings: params.has_warnings ? 1 : undefined,
    sort: params.sort,
    per_page: params.per_page ?? 25,
  }
}

export function displaysInfiniteOptions(params: DisplayListParams) {
  return infiniteQueryOptions({
    queryKey: displayKeys.list(params),
    queryFn: ({ pageParam, signal }) => api.get<Paginated<Display>>('/displays', { signal, query: { ...toQuery(params), cursor: pageParam } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
  })
}

export function useDisplays(params: DisplayListParams, options: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useInfiniteQuery({ ...displaysInfiniteOptions(params), ...options })
}

/** First page only — for pickers and compact lists. */
export function useDisplayPage(params: DisplayListParams, enabled = true) {
  return useQuery({
    queryKey: [...displayKeys.list(params), 'first'] as const,
    queryFn: ({ signal }) => api.get<Paginated<Display>>('/displays', { signal, query: toQuery(params) }),
    select: (page) => page.data,
    enabled,
  })
}

export const displaySummaryOptions = queryOptions({
  queryKey: displayKeys.summary(),
  queryFn: async ({ signal }) => {
    // Accept both `{data: {...}}` and a bare object — the contract shows the bare shape.
    const res = await api.get<DisplaySummary | DataEnvelope<DisplaySummary>>('/displays/summary', { signal })
    return 'data' in res && res.data && typeof res.data === 'object' ? res.data : (res as DisplaySummary)
  },
  refetchInterval: 30_000,
})

export function useDisplaySummary(enabled = true) {
  return useQuery({ ...displaySummaryOptions, enabled })
}

export function displayOptions(id: string) {
  return queryOptions({
    queryKey: displayKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Display>>(`/displays/${id}`, { signal })),
    // Live-ish device page: poll while the tab is visible (TanStack pauses in background tabs).
    refetchInterval: 15_000,
  })
}

export function useDisplay(id: string, enabled = true) {
  return useQuery({ ...displayOptions(id), enabled: enabled && !!id })
}

export function useDisplayAssignments(id: string) {
  return useQuery({
    queryKey: displayKeys.assignments(id),
    queryFn: async ({ signal }) => {
      const res = await api.get<DataEnvelope<DisplayAssignment[]> | Paginated<DisplayAssignment>>(`/displays/${id}/assignments`, { signal })
      return res.data
    },
  })
}

export function useDisplayCommands(id: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: displayKeys.commands(id),
    queryFn: ({ pageParam, signal }) => api.get<Paginated<DisplayCommand>>(`/displays/${id}/commands`, { signal, query: { cursor: pageParam, per_page: 20 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
    enabled,
    // Poll every 5 s while any visible command is still in flight.
    refetchInterval: (query) => (hasActiveCommands(query.state.data?.pages.flatMap((p) => p.data)) ? 5_000 : false),
  })
}

/** Latest commands page — used to follow an in-flight command (there is no `GET /commands/{id}`). */
export function useRecentCommands(
  displayId: string,
  options: { enabled?: boolean; refetchInterval?: number | false; type?: DisplayCommand['type'] } = {},
) {
  return useQuery({
    // Under `commands(id)` so issuing/canceling (which invalidate that prefix) refreshes it too.
    queryKey: [...displayKeys.commands(displayId), 'recent', options.type ?? 'all'] as const,
    queryFn: ({ signal }) =>
      api.get<Paginated<DisplayCommand>>(`/displays/${displayId}/commands`, { signal, query: { per_page: 25, type: options.type } }),
    select: (page) => page.data,
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval ?? false,
  })
}

/** Signed screenshot URLs live 10 minutes: refresh well before that. */
export const SCREENSHOT_URL_STALE_MS = 4 * 60_000

export function useDisplayScreenshots(displayId: string, options: { enabled?: boolean; refetchInterval?: number | false; limit?: number } = {}) {
  return useQuery({
    queryKey: [...displayKeys.screenshots(displayId), options.limit ?? 20] as const,
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Screenshot[]>>(`/displays/${displayId}/screenshots`, { signal, query: { limit: options.limit ?? 20 } })),
    enabled: options.enabled ?? true,
    staleTime: SCREENSHOT_URL_STALE_MS,
    // Even when idle, re-sign before the URLs on screen expire.
    refetchInterval: options.refetchInterval || SCREENSHOT_URL_STALE_MS,
  })
}

export const EVENTS_PAGE_SIZE = 50

export function useDisplayEvents(displayId: string, severities: readonly EventSeverity[] | undefined, enabled = true) {
  return useInfiniteQuery({
    queryKey: displayKeys.events(displayId, severities),
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        api.get<DataEnvelope<DisplayEvent[]>>(`/displays/${displayId}/events`, {
          signal,
          query: { 'severity[]': severities ?? [], before: pageParam?.before, before_id: pageParam?.before_id, limit: EVENTS_PAGE_SIZE },
        }),
      ),
    initialPageParam: undefined as EventsCursor | undefined,
    getNextPageParam: (last) => nextBeforeCursor(last, EVENTS_PAGE_SIZE),
    enabled,
    refetchInterval: 30_000,
  })
}

/* --------------------------------------------------------------- mutations */

export function invalidateDisplays(qc: QueryClient, id?: string) {
  void qc.invalidateQueries({ queryKey: displayKeys.lists() })
  void qc.invalidateQueries({ queryKey: displayKeys.summary() })
  if (id) void qc.invalidateQueries({ queryKey: displayKeys.detail(id) })
  void qc.invalidateQueries({ queryKey: ['dashboard'] })
  void qc.invalidateQueries({ queryKey: ['players'] })
  void qc.invalidateQueries({ queryKey: ['groups'] })
  void qc.invalidateQueries({ queryKey: ['tags'] })
  void qc.invalidateQueries({ queryKey: ['locations'] })
}

function invalidateUsage(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: authKeys.me() })
  void qc.invalidateQueries({ queryKey: ['onboarding'] })
}

export function useCreateDisplay() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: DisplayInput) => unwrap(api.post<DataEnvelope<Display>>('/displays', input)),
    onSuccess: (display) => {
      qc.setQueryData(displayKeys.detail(display.id), display)
      invalidateDisplays(qc)
      invalidateUsage(qc)
    },
  })
}

export function useUpdateDisplay(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<DisplayInput>) => unwrap(api.patch<DataEnvelope<Display>>(`/displays/${id}`, input)),
    onSuccess: (display) => {
      // Keep telemetry from the detail payload if the PATCH response omits it.
      qc.setQueryData<Display>(displayKeys.detail(id), (prev) =>
        prev?.player && display.player && display.player.telemetry === undefined ? { ...display, player: { ...display.player, telemetry: prev.player.telemetry } } : display,
      )
      invalidateDisplays(qc, id)
    },
  })
}

export function useDeleteDisplay() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/displays/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: displayKeys.lists() })
      const snapshot = qc.getQueriesData<InfiniteData<Paginated<Display>>>({ queryKey: displayKeys.lists() })
      qc.setQueriesData<InfiniteData<Paginated<Display>>>({ queryKey: displayKeys.lists() }, (data) =>
        data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.filter((d) => d.id !== id) })) } : data,
      )
      return { snapshot }
    },
    onError: (_e, _id, ctx) => ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSuccess: (_d, id) => {
      qc.removeQueries({ queryKey: displayKeys.detail(id) })
      invalidateUsage(qc)
    },
    onSettled: () => invalidateDisplays(qc),
  })
}

export function useUnassignPlayer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<unknown>(`/displays/${id}/unassign`),
    onSuccess: () => {
      invalidateDisplays(qc, id)
      invalidateUsage(qc)
    },
  })
}

export function useIssueCommand(displayId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ type, payload }: { type: CommandType; payload?: Record<string, unknown> }) =>
      unwrap(api.post<DataEnvelope<DisplayCommand>>(`/displays/${displayId}/commands`, payload ? { type, payload } : { type })),
    onSuccess: (command) => {
      // Prepend so the history shows it immediately (and polling kicks in).
      qc.setQueryData<InfiniteData<Paginated<DisplayCommand>>>(displayKeys.commands(displayId), (data) =>
        data?.pages?.[0] ? { ...data, pages: [{ ...data.pages[0], data: [command, ...data.pages[0].data.filter((c) => c.id !== command.id)] }, ...data.pages.slice(1)] } : data,
      )
      void qc.invalidateQueries({ queryKey: displayKeys.commands(displayId) })
    },
  })
}

export function useCancelCommand(displayId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (commandId: string) => api.post<unknown>(`/commands/${commandId}/cancel`),
    onSettled: () => void qc.invalidateQueries({ queryKey: displayKeys.commands(displayId) }),
  })
}
